import { useRef, useEffect, useLayoutEffect, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiSend, FiRefreshCw } from 'react-icons/fi';
import {
  FaBug,
  FaLightbulb,
  FaCreditCard,
  FaHandshake,
  FaBuilding,
  FaStar,
  FaBookOpen,
} from 'react-icons/fa';
import { useSupportChatStore, type SupportMessage } from '../../stores/support-chat.store';
import { REFUSAL_PROMPTED } from '../../services/api';
import ProposalCard from './ProposalCard';
import { useSubscriptionStore } from '../../stores/subscription.store';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import OutOfTokensModal from '../OutOfTokensModal';

const QUICK_ACTIONS = [
  { label: 'Report a bug', icon: FaBug, message: "I'd like to report a bug I encountered." },
  {
    label: 'Request a feature',
    icon: FaLightbulb,
    message: "I have a feature request I'd like to share.",
  },
  {
    label: 'Plans & billing',
    icon: FaCreditCard,
    message: 'Can you tell me about the different plans and pricing?',
  },
  {
    label: 'Partnership',
    icon: FaHandshake,
    message:
      "I'm interested in partnering with EmpathEase. I'd like to discuss collaboration opportunities.",
  },
  {
    label: 'Testimonial',
    icon: FaStar,
    message: "I'd like to share a short testimonial about how EmpathEase has helped me.",
  },
  {
    label: 'Share my story',
    icon: FaBookOpen,
    message:
      "I'd like to write about how EmpathEase changed my life — a longer story that could be shared or published.",
  },
  {
    label: 'Enterprise',
    icon: FaBuilding,
    message:
      "I'm interested in EmpathEase for my team or organization. Can you tell me about enterprise licensing?",
  },
];

// Regex to detect in-app links in message content
const APP_LINK_REGEX = /(\/app(?:\/[\w-]+)*)/g;

function AppLink({ to }: { to: string }) {
  const navigate = useNavigate();
  const setOpen = useSupportChatStore((s) => s.setOpen);

  return (
    <button
      onClick={() => {
        setOpen(false);
        void navigate(to);
      }}
      className="text-primary underline hover:text-primary/80"
    >
      {to}
    </button>
  );
}

function renderMessageContent(content: string) {
  // Remove all action markers from displayed content
  const cleanContent = content
    .replace(
      /\|\|\|(?:BUG_REPORT|FEATURE_REQUEST|PARTNERSHIP_INQUIRY|ENTERPRISE_LICENSING|TESTIMONIAL|CONTENT_CONTRIBUTION|PRESS_INQUIRY|INTEGRATION_REQUEST|SPEAKER_REQUEST)\|\|\|.*/s,
      '',
    )
    .trim();

  // Split by app links and render them as clickable
  const parts = cleanContent.split(APP_LINK_REGEX);

  return parts.map((part, i) => {
    if (APP_LINK_REGEX.test(part)) {
      // Reset regex lastIndex since it's global
      APP_LINK_REGEX.lastIndex = 0;
      // eslint-disable-next-line react/no-array-index-key -- Why: text-split tokens by regex; positional fragments of a single message string that never reorders
      return <AppLink key={i} to={part} />;
    }
    // eslint-disable-next-line react/no-array-index-key -- Why: text-split tokens by regex; positional fragments of a single message string that never reorders
    return <span key={i}>{part}</span>;
  });
}

function MessageBubble({
  message,
  messageIndex,
}: {
  message: SupportMessage;
  messageIndex: number;
}) {
  const isUser = message.role === 'user';
  const submitProposal = useSupportChatStore((s) => s.submitProposal);
  const editProposal = useSupportChatStore((s) => s.editProposal);
  const cancelProposal = useSupportChatStore((s) => s.cancelProposal);

  return (
    <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} mb-3`}>
      <div
        className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? 'bg-primary text-(--ee-on-accent) rounded-br-md'
            : 'bg-(--ee-tw-gray-100-bg) text-(--ee-tw-gray-800) rounded-bl-md'
        }`}
      >
        {isUser ? message.content : renderMessageContent(message.content)}
      </div>
      {message.proposal ? (
        <div className="w-full max-w-[85%]">
          <ProposalCard
            proposal={message.proposal}
            onSubmit={() => void submitProposal(messageIndex)}
            onEdit={() => editProposal(messageIndex)}
            onCancel={() => cancelProposal(messageIndex)}
          />
        </div>
      ) : null}
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex justify-start mb-3">
      <div className="bg-(--ee-tw-gray-100-bg) px-4 py-3 rounded-2xl rounded-bl-md flex gap-1.5">
        <span
          className="w-2 h-2 bg-(--ee-tw-gray-400-bg) rounded-full animate-bounce"
          style={{ animationDelay: '0ms' }}
        />
        <span
          className="w-2 h-2 bg-(--ee-tw-gray-400-bg) rounded-full animate-bounce"
          style={{ animationDelay: '150ms' }}
        />
        <span
          className="w-2 h-2 bg-(--ee-tw-gray-400-bg) rounded-full animate-bounce"
          style={{ animationDelay: '300ms' }}
        />
      </div>
    </div>
  );
}

export default function SupportChatPanel() {
  const isDesktop = useIsDesktop();
  const { messages, isLoading, sendMessage, resetConversation, setOpen } = useSupportChatStore();
  const usage = useSubscriptionStore((s) => s.usage);
  const canUseCredits = useSubscriptionStore((s) => s.canUseCredits);

  const [input, setInput] = useState('');
  const [showOutOfTokens, setShowOutOfTokens] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const panelVariants = isDesktop
    ? {
        initial: { opacity: 0, scale: 0.95, y: 20 },
        animate: { opacity: 1, scale: 1, y: 0 },
        exit: { opacity: 0, scale: 0.95, y: 20 },
      }
    : {
        initial: { y: '100%' },
        animate: { y: 0 },
        exit: { y: '100%' },
      };

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    // Check credits before sending
    if (!canUseCredits(1)) {
      setShowOutOfTokens(true);
      return;
    }

    setInput('');
    void sendMessage(trimmed).then((outcome) => {
      // The global UpgradePrompt answered the refusal and nothing was sent:
      // the draft goes back (unless a new one was started meanwhile).
      if (outcome === REFUSAL_PROMPTED) setInput((now) => (now === '' ? trimmed : now));
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickAction = (message: string) => {
    if (isLoading) return;
    if (!canUseCredits(1)) {
      setShowOutOfTokens(true);
      return;
    }
    void sendMessage(message);
  };

  const handleTextareaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };

  // Auto-resize the textarea to its text, whatever set it: typing, the send
  // clearing it, or a refused send putting the draft back (b-185).
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [input]);

  return (
    <>
      {/* Backdrop for mobile */}
      {!isDesktop && (
        <motion.div
          className="fixed inset-0 z-(--ee-z-panel) bg-(--ee-scrim)/40"
          onClick={() => setOpen(false)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        />
      )}
      <motion.div
        className={`fixed z-(--ee-z-panel) bg-(--ee-surface) flex flex-col shadow-2xl ${
          isDesktop
            ? 'bottom-20 right-4 w-96 h-[500px] rounded-xl border border-(--ee-tw-gray-200-line) origin-bottom-right'
            : 'inset-x-0 bottom-0 h-[85vh] rounded-t-2xl'
        }`}
        variants={panelVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-(--ee-tw-gray-100-line) shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <span className="text-(--ee-on-accent) text-xs font-bold">AI</span>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-(--ee-tw-gray-900)">AI Support</h3>
              <p className="text-[10px] text-(--ee-tw-gray-500)">Bug reports, features, billing</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button
                onClick={resetConversation}
                className="p-2 text-(--ee-tw-gray-400) hover:text-(--ee-tw-gray-600) hover:bg-(--ee-tw-gray-100-bg) rounded-lg transition-colors"
                title="New conversation"
                aria-label="Start new conversation"
              >
                <FiRefreshCw size={16} />
              </button>
            )}
            <button
              onClick={() => setOpen(false)}
              className="p-2 text-(--ee-tw-gray-400) hover:text-(--ee-tw-gray-600) hover:bg-(--ee-tw-gray-100-bg) rounded-lg transition-colors"
              aria-label="Close support chat"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <div className="text-center">
                <p className="text-sm text-(--ee-tw-gray-600) mb-1">How can I help you?</p>
                <p className="text-xs text-(--ee-tw-gray-400)">
                  Choose a topic or type your question
                </p>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 w-full">
                {QUICK_ACTIONS.map((action) => (
                  <button
                    key={action.label}
                    onClick={() => handleQuickAction(action.message)}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-(--ee-tw-gray-200-line) hover:border-primary/30 hover:bg-primary/5 transition-all text-center group"
                  >
                    <action.icon
                      className="text-(--ee-tw-gray-400) group-hover:text-primary transition-colors"
                      size={18}
                    />
                    <span className="text-xs font-medium text-(--ee-tw-gray-600) group-hover:text-(--ee-tw-gray-900)">
                      {action.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg, i) => (
                // eslint-disable-next-line react/no-array-index-key -- Why: chat history is append-only; last message is replaced in place during SSE streaming, never reordered/inserted
                <MessageBubble key={i} message={msg} messageIndex={i} />
              ))}
              {isLoading && messages[messages.length - 1]?.role !== 'assistant' ? (
                <TypingIndicator />
              ) : null}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input Area */}
        <div className="border-t border-(--ee-tw-gray-100-line) px-3 py-2 shrink-0">
          <div className="flex items-end gap-2">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={handleTextareaInput}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              aria-label="Message input"
              rows={1}
              className="flex-1 resize-none border border-(--ee-tw-gray-200-line) rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 max-h-[120px]"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              className="p-2.5 rounded-xl bg-primary text-(--ee-on-accent) disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors shrink-0"
            >
              <FiSend size={18} />
            </button>
          </div>
        </div>
      </motion.div>

      <OutOfTokensModal
        isOpen={showOutOfTokens}
        onClose={() => setShowOutOfTokens(false)}
        usage={usage}
        tokenType="credits"
        gaFeature="support_chat"
      />
    </>
  );
}
